import type { OutputFormat } from '../types/floorPlan.types';

const MAX_CANVAS_DIMENSION = 16_384;
export const DEFAULT_SCALE_FACTOR = 1;
export const DEFAULT_QUALITY = 100;

type MimeType = 'image/webp' | 'image/png' | 'image/jpeg';

const FORMAT_TO_MIME: Record<OutputFormat, MimeType> = {
  webp: 'image/webp',
  png: 'image/png',
  jpg: 'image/jpeg',
};

const MIME_TO_EXT: Record<string, OutputFormat> = {
  'image/webp': 'webp',
  'image/png': 'png',
  'image/jpeg': 'jpg',
};

export type CompressImageOptions = {
  scaleFactor: number;
  quality: number;
  outputFormat: OutputFormat;
};

export type CompressImageResult = {
  blob: Blob;
  file: File;
};

/**
 * Returns whether the given settings require client-side image processing.
 */
export function needsClientProcessing(
  options: CompressImageOptions,
  originalMimeType: string,
): boolean {
  const originalFormat = MIME_TO_EXT[originalMimeType];
  return (
    options.scaleFactor !== DEFAULT_SCALE_FACTOR ||
    options.quality !== DEFAULT_QUALITY ||
    options.outputFormat !== originalFormat
  );
}

/**
 * Computes the maximum scale factor allowed for an image given canvas limits.
 */
export function getMaxScaleFactor(
  naturalWidth: number,
  naturalHeight: number,
): number {
  const maxByWidth = MAX_CANVAS_DIMENSION / naturalWidth;
  const maxByHeight = MAX_CANVAS_DIMENSION / naturalHeight;
  return Math.min(maxByWidth, maxByHeight);
}

/**
 * Checks whether a scaled image will exceed canvas limits.
 */
export function exceedsCanvasLimits(
  naturalWidth: number,
  naturalHeight: number,
  scaleFactor: number,
): boolean {
  return (
    naturalWidth * scaleFactor > MAX_CANVAS_DIMENSION ||
    naturalHeight * scaleFactor > MAX_CANVAS_DIMENSION
  );
}

/**
 * Compresses/resizes an image in the browser using createImageBitmap + Canvas.
 * Handles arbitrarily large input images by resizing during decode.
 */
export async function compressImage(
  file: File,
  naturalWidth: number,
  naturalHeight: number,
  options: CompressImageOptions,
): Promise<CompressImageResult> {
  const { scaleFactor, quality, outputFormat } = options;
  const targetMime = FORMAT_TO_MIME[outputFormat];

  const targetWidth = Math.round(naturalWidth * scaleFactor);
  const targetHeight = Math.round(naturalHeight * scaleFactor);

  // Decode + resize in one step — never allocates full-resolution bitmap
  const bitmap = await createImageBitmap(file, {
    resizeWidth: targetWidth,
    resizeHeight: targetHeight,
    resizeQuality: 'high',
  });

  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas 2d context');

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  // Quality parameter: 0-1 for lossy formats, ignored for PNG
  const qualityParam = outputFormat === 'png' ? undefined : quality / 100;
  const blob = await canvas.convertToBlob({
    type: targetMime,
    quality: qualityParam,
  });

  const outputFile = new File([blob], `compressed.${outputFormat}`, {
    type: targetMime,
  });

  return { blob, file: outputFile };
}
